import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import {
  IconProducts,
  IconCategories,
  IconBrands,
  IconCustomers,
  IconShipping,
  IconOrders,
  IconConfig,
  IconShippingZone,
  IconHome,
  IconDashboard,
  IconPaymentMethod,
  IconStore,
} from "../../icons/Icons";
import { hasPerm } from "../../utils/hasPerm";
import { CELESTE } from "./sidebarTheme";

const BORDER = "#ececf1";

const getInitials = (nombre, apellido) => {
  if (!nombre || !apellido) return "U";
  return `${nombre[0]}${apellido[0]}`.toUpperCase();
};

const ChevronLeft = (
  <svg width="20" height="20" stroke={CELESTE.accentDark} strokeWidth="2.3" fill="none" viewBox="0 0 24 24">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRight = (
  <svg width="20" height="20" stroke={CELESTE.accentDark} strokeWidth="2.3" fill="none" viewBox="0 0 24 24">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const Hamburger = (
  <svg width="20" height="20" stroke={CELESTE.accentDark} strokeWidth="2.3" fill="none" viewBox="0 0 24 24">
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

function Sidebar({ collapsed, setCollapsed }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("tienda_data") || "null");
    } catch {
      return null;
    }
  });

  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      setUser(JSON.parse(localStorage.getItem("tienda_data") || "null"));
    } catch {
      setUser(null);
    }

    const onStorage = (e) => {
      if (e.key === "tienda_data") {
        try {
          setUser(JSON.parse(e.newValue || "null"));
        } catch {
          setUser(null);
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const apply = () => {
      const mobile = mq.matches;
      setIsMobile(mobile);
      if (mobile) setMobileOpen(false);
    };

    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileOpen]);

  const logout = () => {
    localStorage.removeItem("tienda_data");
    localStorage.removeItem("tienda_usuario_id");
    localStorage.removeItem("tienda_token");
    localStorage.removeItem("tienda_permisos");
    localStorage.removeItem("tienda_id");
    localStorage.removeItem("newSession");
    window.location.href = "/administracion/tienda/login";
  };

  const uiCollapsed = isMobile ? false : collapsed;
  const onNavigate = isMobile ? () => setMobileOpen(false) : undefined;

  const goToProfile = () => {
    if (isMobile) setMobileOpen(false);
    navigate("/administracion/tienda/perfil");
  };

  const shellStyle = useMemo(() => {
    const base = {
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      zIndex: 3001,
      background: "#ffffff",
      width: "100%",
      minWidth: 0,
      borderRight: `1px solid ${BORDER}`,
    };

    if (!isMobile) return base;

    return {
      ...base,
      position: "fixed",
      top: 0,
      left: 0,
      width: "min(86vw, 320px)",
      transform: mobileOpen ? "translateX(0)" : "translateX(-102%)",
      transition: "transform 220ms ease",
      boxShadow: mobileOpen ? "0 18px 50px rgba(15, 23, 42, 0.22)" : "none",
    };
  }, [isMobile, mobileOpen]);

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    backdropFilter: "blur(2px)",
    zIndex: 3000,
  };

  const mobileFabStyle = {
    position: "fixed",
    top: "14px",
    left: "14px",
    zIndex: 4000,
    background: "#ffffff",
    border: `1px solid ${BORDER}`,
    padding: "12px",
    borderRadius: "14px",
    cursor: "pointer",
    color: CELESTE.accentDark,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <>
      {isMobile && !mobileOpen && (
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
          style={mobileFabStyle}
        >
          {Hamburger}
        </button>
      )}

      {isMobile && mobileOpen && (
        <div style={overlayStyle} onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      <div style={shellStyle} role="navigation" aria-label="Admin sidebar">
        <div
          style={{
            padding: uiCollapsed ? "40px 8px" : "22px 20px",
            borderBottom: `1px solid ${BORDER}`,
            position: "relative",
            background: "#ffffff",
          }}
        >
          <button
            onClick={() => {
              if (isMobile) {
                setMobileOpen(false);
                return;
              }
              setCollapsed(!collapsed);
            }}
            style={{
              position: "absolute",
              top: "18px",
              right: uiCollapsed ? "50%" : "12px",
              transform: uiCollapsed ? "translateX(50%)" : "none",
              background: CELESTE.bg,
              border: `1px solid ${CELESTE.borderSoft}`,
              padding: "9px",
              borderRadius: "12px",
              cursor: "pointer",
              transition: "0.2s ease",
              color: CELESTE.accentDark,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = CELESTE.bgHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = CELESTE.bg)}
            type="button"
          >
            {isMobile ? ChevronLeft : collapsed ? ChevronRight : ChevronLeft}
          </button>

          {!uiCollapsed && (
            <div style={{ marginTop: "48px", marginBottom: "4px" }}>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "800",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: CELESTE.accent,
                  marginBottom: "6px",
                }}
              >
                Tienda
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#1e293b",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.25,
                  wordBreak: "break-word",
                }}
                title={(user?.tienda_nombre || "").trim() || undefined}
              >
                {(user?.tienda_nombre || "").trim() ||
                  (user?.tienda_id != null && user.tienda_id !== ""
                    ? `Tienda #${user.tienda_id}`
                    : "Mi tienda")}
              </h2>
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: uiCollapsed ? "16px 8px" : "16px 14px",
            background: "#ffffff",
          }}
        >
          <SidebarItem
            icon={IconHome}
            label="Inicio"
            to="/administracion/tienda"
            end
            collapsed={uiCollapsed}
            onNavigate={onNavigate}
          />

          {hasPerm("view_orders") && (
            <SidebarItem
              icon={IconDashboard}
              label="Dashboard"
              to="/administracion/tienda/dashboard"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}

          {hasPerm("view_orders") && (
            <SidebarItem
              icon={IconOrders}
              label="Pedidos"
              to="/administracion/tienda/orders"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}

          {hasPerm("view_product") && (
            <SidebarItem
              icon={IconProducts}
              label="Productos"
              to="/administracion/tienda/products"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}

          {hasPerm("view_categories") && (
            <SidebarItem
              icon={IconCategories}
              label="Categorias"
              to="/administracion/tienda/categories"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}

          {hasPerm("view_brand") && (
            <SidebarItem
              icon={IconBrands}
              label="Marcas"
              to="/administracion/tienda/brands"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}

          {hasPerm("view_customer") && (
            <SidebarItem
              icon={IconCustomers}
              label="Clientes"
              to="/administracion/tienda/customers"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}

          {hasPerm("view_shipping_methods") && (
            <SidebarItem
              icon={IconPaymentMethod}
              label="Metodos de pago"
              to="/administracion/tienda/payment-methods"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}

          {hasPerm("view_payment_methods") && (
            <SidebarItem
              icon={IconShipping}
              label="Metodos de envio"
              to="/administracion/tienda/shipping-methods"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}
          {hasPerm("view_shipping_zones") && (
            <SidebarItem
              icon={IconShippingZone}
              label="Zonas de envio"
              to="/administracion/tienda/zona-envios"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}
          {hasPerm("view_shipping_zones") && (
            <SidebarItem
              icon={IconStore}
              label="Tienda"
              to="/administracion/tienda/tienda"
              collapsed={uiCollapsed}
              onNavigate={onNavigate}
            />
          )}
          <SidebarItem
            icon={IconConfig}
            label="Configuracion"
            collapsed={uiCollapsed}
            onNavigate={onNavigate}
            children={[
              hasPerm("view_user") && { label: "Usuarios", to: "/administracion/tienda/config/users" },
              hasPerm("view_rol") && { label: "Roles", to: "/administracion/tienda/config/roles" },
            ].filter(Boolean)}
          />
        </div>

        <div
          style={{
            padding: uiCollapsed ? "14px 8px" : "14px 16px",
            borderTop: `1px solid ${BORDER}`,
            background: "#fafafb",
            display: "flex",
            alignItems: "center",
            justifyContent: uiCollapsed ? "center" : "space-between",
            gap: "10px",
          }}
        >
          <button
            type="button"
            onClick={goToProfile}
            title="Editar mi perfil"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: uiCollapsed ? "0 0 auto" : 1,
              minWidth: 0,
              background: "transparent",
              border: "none",
              borderRadius: "12px",
              padding: uiCollapsed ? "4px" : "6px 8px",
              cursor: "pointer",
              textAlign: "left",
              transition: "background 0.18s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = CELESTE.bgSoft)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div
              style={{
                width: uiCollapsed ? "38px" : "42px",
                height: uiCollapsed ? "38px" : "42px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${CELESTE.accentLight} 0%, ${CELESTE.accent} 100%)`,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "700",
                fontSize: uiCollapsed ? "14px" : "16px",
                transition: "0.3s ease",
                boxShadow: `0 3px 10px ${CELESTE.shadow}`,
                flex: "0 0 auto",
              }}
            >
              {getInitials(user?.nombre, user?.apellido)}
            </div>

            {!uiCollapsed && (
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    color: "#1e293b",
                    fontWeight: "600",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={`${user?.nombre || ""} ${user?.apellido || ""}`.trim()}
                >
                  {user?.nombre} {user?.apellido}
                </div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: CELESTE.accent, marginTop: "1px" }}>
                  Ver perfil
                </div>
              </div>
            )}
          </button>

          {!uiCollapsed && (
            <button
              onClick={logout}
              style={{
                background: "#ffffff",
                borderRadius: "10px",
                padding: "8px 10px",
                cursor: "pointer",
                color: "#ef4444",
                transition: "0.2s ease",
                border: "1px solid #fecaca",
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fef2f2";
                e.currentTarget.style.borderColor = "#fca5a5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.borderColor = "#fecaca";
              }}
              type="button"
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default Sidebar;
